/**
 * Bullhorn JobSubmission → status timeline (JobSubmissionHistory via REST `history` field, API 2025.5+).
 * Handles association shapes: bare array, { data: [...] }, or nested total/data.
 */

export interface JobSubmissionStatusEvent {
  bullhornHistoryId?: number;
  status: string;
  dateAdded: string | null;
  comments: string | null;
  transactionId: string | null;
  modifiedBy: { id?: number; firstName?: string; lastName?: string } | null;
}

function asArray<T>(x: T[] | { data?: T[]; total?: number } | null | undefined): T[] {
  if (x == null) return [];
  if (Array.isArray(x)) return x;
  if (typeof x === 'object' && Array.isArray((x as { data?: T[] }).data)) {
    return (x as { data: T[] }).data;
  }
  return [];
}

/**
 * Bullhorn REST returns `dateAdded` as epoch milliseconds (number), or ISO-like strings.
 * Numeric strings (only digits) are treated the same; values below 1e12 are assumed seconds.
 */
export function normalizeBullhornTimestamp(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 0 && raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return null;
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      const ms = n > 0 && n < 1e12 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return new Date(t).toISOString();
  }
  return null;
}

/** Encode Bullhorn `fields` parameter (brackets/parens in query string). */
export function encodeBullhornQueryFields(fields: string): string {
  return fields
    .replace(/\[/g, '%5B')
    .replace(/\]/g, '%5D')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
}

const HISTORY_FIELDS =
  'history[200](id,status,dateAdded,comments,transactionID,modifyingUser(id,firstName,lastName))';

/** Fields for query/JobSubmission when scoped by job order */
export const JOB_SUBMISSION_FIELDS_BY_JOB = [
  'id',
  'status',
  'dateAdded',
  'candidate(id,firstName,lastName,email,phone,mobile)',
  HISTORY_FIELDS,
].join(',');

/** Fields when scoped by candidate */
export const JOB_SUBMISSION_FIELDS_BY_CANDIDATE = [
  'id',
  'jobOrder(id,title,status)',
  'status',
  'dateAdded',
  HISTORY_FIELDS,
].join(',');

/** Same as above but without `history` (older Bullhorn stacks). */
export const JOB_SUBMISSION_FIELDS_BY_JOB_NO_HISTORY =
  'id,status,dateAdded,candidate(id,firstName,lastName,email,phone,mobile)';
export const JOB_SUBMISSION_FIELDS_BY_CANDIDATE_NO_HISTORY =
  'id,jobOrder(id,title,status),status,dateAdded';

/**
 * Normalize JobSubmissionHistory rows from a JobSubmission API record into a chronological list.
 */
export function normalizeJobSubmissionHistory(rawSubmission: unknown): JobSubmissionStatusEvent[] {
  if (!rawSubmission || typeof rawSubmission !== 'object') return [];
  const sub = rawSubmission as Record<string, unknown>;
  const rawHist = sub.history;
  const rows = asArray<Record<string, unknown>>(rawHist as never);

  const events: JobSubmissionStatusEvent[] = rows.map((row) => {
    const mu = row.modifyingUser as Record<string, unknown> | undefined;
    return {
      bullhornHistoryId: typeof row.id === 'number' ? row.id : row.id != null ? Number(row.id) : undefined,
      status: String(row.status ?? ''),
      dateAdded: normalizeBullhornTimestamp(row.dateAdded),
      comments: row.comments != null ? String(row.comments) : null,
      transactionId: row.transactionID != null ? String(row.transactionID) : null,
      modifiedBy: mu
        ? {
            id: typeof mu.id === 'number' ? mu.id : mu.id != null ? Number(mu.id) : undefined,
            firstName: mu.firstName != null ? String(mu.firstName) : undefined,
            lastName: mu.lastName != null ? String(mu.lastName) : undefined,
          }
        : null,
    };
  });

  events.sort((a, b) => {
    const ta = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
    const tb = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
    return ta - tb;
  });

  return events;
}
